/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button, DatePicker, Form, message, Modal, Select, Table, Tag } from "antd";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import dayjs from "dayjs";
import { format } from "date-fns";
import { _get, _put } from "../../../Utils/Helper";

import "leaflet/dist/leaflet.css";

const QRCodeExpiryTabNew = () => {
  const [expiryForm] = Form.useForm();
  const [qrCodes, setQrCodes] = useState<QRCode[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("view");
  const [selectedQRCode, setSelectedQRCode] = useState<QRCode | null>(null);

  interface QRCode {
    id: string;
    qrCode: string;
    user_type: string;
    expiry_datetime: string;
    location: string;
    status: string;
  }
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);

  useEffect(() => {
    fetchAllQRCodes();
  }, []);

  const fetchAllQRCodes = () => {
    _get(
      "get-all-qrcodes",
      (response) => {
        if (Array.isArray(response)) {
          setQrCodes(response.map(mapQRCode));
        } else if (response && typeof response === "object") {
          setQrCodes([mapQRCode(response)]);
        } else {
          console.error("Unexpected response format:", response);
          message.error("Invalid data received from the server.");
        }
      },
      (error) => message.error("Failed to fetch QR codes.")
    );
  };

  const mapQRCode = (item: { id: any; qrcode: any; usertype: any; expiry_datetime: any; location_is_boolean: any; latitude: any; longitude: any; status: any; }) => ({
    id: item.id,
    qrCode: item.qrcode,
    user_type: item.usertype,
    expiry_datetime: item.expiry_datetime,
    location: item.location_is_boolean ? `${item.latitude}, ${item.longitude}` : "Not Required",
    status: item.status,
  });

  const handleOpenModal = (type: React.SetStateAction<string>, record: QRCode) => {
    setModalType(type);
    setSelectedQRCode(record);
    setShowModal(true);

    // Preload latitude and longitude for editing
    if (record && record.location !== "Not Required") {
      const [lat, lon] = record.location.split(", ");
      setLatitude(parseFloat(lat));
      setLongitude(parseFloat(lon));
    }
  };

  const handleSaveChanges = (values: { expiry_datetime: string | number | Date; }) => {
    if (!selectedQRCode) {
      message.error("No QR Code selected.");
      return;
    }

    // Format expiry_datetime to MySQL-compatible format
    const formattedExpiryDatetime = values.expiry_datetime
      ? format(new Date(values.expiry_datetime), "yyyy-MM-dd HH:mm:ss")
      : selectedQRCode?.expiry_datetime;

    const updatedData = {
      ...(typeof selectedQRCode === "object" && selectedQRCode !== null ? selectedQRCode : {}),
      ...values,
      latitude,
      longitude,
      expiry_datetime: formattedExpiryDatetime,
    };

    _put(
      `regenerate-qrcode/${selectedQRCode.id}`,
      updatedData,
      () => {
        message.success("QR Code updated successfully.");
        fetchAllQRCodes();
        setShowModal(false);
      },
      () => message.error("Failed to update QR Code.")
    );
  };

  const fetchCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
          message.success("Current location fetched successfully.");
        },
        (error) => {
          message.error("Failed to fetch current location: " + error.message);
        }
      );
    } else {
      message.error("Geolocation is not supported by your browser.");
    }
  };

  const MapEvents = () => {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        setLatitude(lat);
        setLongitude(lng);
      },
    });
    return null;
  };

  const columns = [
    {
      title: "QR Code",
      dataIndex: "qrCode",
      render: (value: string | string[]) => <QRCodeSVG value={value} size={100} />,
    },
    { title: "User Type", dataIndex: "user_type" },
    {
      title: "Expiry",
      dataIndex: "expiry_datetime",
      render: (value: string | number | Date) => format(new Date(value), "PPpp"),
    },
    { title: "Location", dataIndex: "location" },
    {
      title: "Status",
      dataIndex: "status",
      render: (value: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | null | undefined) => <Tag color={value === "active" ? "green" : "orange"}>{value}</Tag>,
    },
    {
      title: "Actions",
      render: (_: any, record: any) => (
        <>
          <Button onClick={() => handleOpenModal("view", record)}>View</Button>
          <Button onClick={() => handleOpenModal("edit", record)}>Edit</Button>
        </>
      ),
    },
  ];

  return (
    <div>
      <Table columns={columns} dataSource={qrCodes} rowKey="id" />
      <Modal
        visible={showModal}
        title={modalType === "view" ? "View QR Code Details" : "Edit QR Code Details"}
        onCancel={() => setShowModal(false)}
        footer={null}
      >
        {modalType === "view" && selectedQRCode && (
          <div>
            <QRCodeSVG value={selectedQRCode.qrCode} size={200} />
            <p>User Type: {selectedQRCode.user_type}</p>
            <p>Expiry: {format(new Date(selectedQRCode.expiry_datetime), "PPpp")}</p>
            <p>Location: {selectedQRCode.location}</p>
            <p>Status: {selectedQRCode.status}</p>
          </div>
        )}
        {modalType === "edit" && (
          <Form
            form={expiryForm}
            initialValues={{
              ...selectedQRCode,
              expiry_datetime: dayjs(selectedQRCode?.expiry_datetime),
            }}
            onFinish={handleSaveChanges}
          >
            <Form.Item name="user_type" label="User Type">
              <Select>
                <Select.Option value="teacher">Teacher</Select.Option>
                <Select.Option value="staff">Staff</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="expiry_datetime" label="Expiry Date">
              <DatePicker showTime />
            </Form.Item>
            <div>
              <p>Latitude: {latitude || "Not Set"}</p>
              <p>Longitude: {longitude || "Not Set"}</p>
              <Button onClick={fetchCurrentLocation}>Use Current Location</Button>
            </div>
            <div style={{ height: "300px", marginTop: "20px" }}>
              <MapContainer
                center={[latitude || 51.505, longitude || -0.09]}
                zoom={13}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                />
                <MapEvents />
                {latitude && longitude && <Marker position={[latitude, longitude]} />}
              </MapContainer>
            </div>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Save Changes
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default QRCodeExpiryTabNew;
